import { getENV } from '@libs/common-next';
import {
    AUTH_HEADERS,
    getAccessToken,
    getAnonymousIdToken,
    getCustomerKey,
    getVisitorId,
    isAccessTokenAuth0,
    isLocaleCA,
} from '@libs/core';

import type { ApiProxyClientOptions } from '../types';
import { createApiProxyClient } from '../utils';
import { buildSourceLocationHeader } from '../utils/source-location';

function requestBuilder<TBody = BodyInit>(options: ApiProxyClientOptions<TBody> = {}): ApiProxyClientOptions<TBody> {
    const accessToken = getAccessToken();
    const customerKey = getCustomerKey();
    const anonymousKey = getAnonymousIdToken();
    const visitorId = getVisitorId();

    const headers: HeadersInit = {
        ...buildSourceLocationHeader(),
        ...(accessToken && { [AUTH_HEADERS.AUTHORIZATION]: `Bearer ${accessToken}` }),
        ...(customerKey && { [AUTH_HEADERS.X_PETM_CUSTOMER_KEY]: customerKey }),
        ...(anonymousKey && !customerKey && !accessToken && { [AUTH_HEADERS.X_PETM_ANONYMOUS_KEY]: anonymousKey }),
        ...(visitorId && { [AUTH_HEADERS.X_PETM_VISITOR_ID]: visitorId }),
    };

    const ephemeralEnv = getENV('NEXT_PUBLIC_EPHEMERAL');
    const sendAuth0Header = Boolean(accessToken) && isAccessTokenAuth0();

    return {
        ...options,
        headers: {
            ...options.headers,
            ...headers,
            ...(ephemeralEnv && { 'x-petm-eph-env': ephemeralEnv }),
            ...(sendAuth0Header && { [AUTH_HEADERS.X_PETM_AUTH0_ENABLED]: 'true' }),
        },
    };
}

function cartsRequestBuilder<TBody = BodyInit>(
    options: ApiProxyClientOptions<TBody> = {}
): ApiProxyClientOptions<TBody> {
    const ephemeralEnv = getENV('NEXT_PUBLIC_EPHEMERAL');

    return {
        ...options,
        headers: {
            ...options.headers,
            ...buildSourceLocationHeader(),
            ...(ephemeralEnv && { 'x-petm-eph-env': ephemeralEnv }),
        },
    };
}

let _cweaClient: ReturnType<typeof createApiProxyClient> | null = null;
let _cweaCartsClient: ReturnType<typeof createApiProxyClient> | null = null;
let _identityClient: ReturnType<typeof createApiProxyClient> | null = null;

function initCweaClient() {
    if (!_cweaClient) {
        const basePath = isLocaleCA()
            ? getENV('NEXT_PUBLIC_CWEA_PROXY_URL_CA')
            : getENV('NEXT_PUBLIC_CWEA_PROXY_URL_US');

        _cweaClient = createApiProxyClient(basePath, requestBuilder);
    }

    return _cweaClient;
}

function initCweaCartsClient() {
    if (!_cweaCartsClient) {
        const basePath = isLocaleCA()
            ? getENV('NEXT_PUBLIC_CWEA_PROXY_URL_CA')
            : getENV('NEXT_PUBLIC_CWEA_PROXY_URL_US');

        _cweaCartsClient = createApiProxyClient(basePath, cartsRequestBuilder);
    }

    return _cweaCartsClient;
}

function initIdentityClient() {
    if (!_identityClient) {
        const ephemeralEnv = getENV('NEXT_PUBLIC_EPHEMERAL');

        let basePath: string;

        if (ephemeralEnv && typeof window !== 'undefined') {
            basePath = `${window.location.origin}/api/identity`;
        } else {
            basePath = isLocaleCA()
                ? getENV('NEXT_PUBLIC_IDENTITY_API_PROXY_URL_CA')
                : getENV('NEXT_PUBLIC_IDENTITY_API_PROXY_URL_US');
        }

        _identityClient = createApiProxyClient(basePath, requestBuilder);
    }

    return _identityClient;
}

// Using a Proxy to lazily initialize the `client` object only when a method is called.
// This avoids the immediate execution of `getENV(...)` (which can throw an error if an env var is missing) at module load time.
// The Proxy ensures that the client is initialized on-demand, allowing for proper testing and environment setup,
// while maintaining a clean API for consumers who can use `client.get(...)` directly without needing to call `client()` first.
export const cweaClient = new Proxy({} as ReturnType<typeof createApiProxyClient>, {
    get(_target, prop) {
        return initCweaClient()[prop as keyof typeof _cweaClient];
    },
});

export const cweaCartsClient = new Proxy({} as ReturnType<typeof createApiProxyClient>, {
    get(_target, prop) {
        return initCweaCartsClient()[prop as keyof typeof _cweaCartsClient];
    },
});

export const identityClient = new Proxy({} as ReturnType<typeof createApiProxyClient>, {
    get(_target, prop) {
        return initIdentityClient()[prop as keyof typeof _identityClient];
    },
});
